/**
 * Cost Register API client: a program's ledger of found, cited prices, reviewed line
 * by line and exported as an IGCE. Backend: `apps/costing` (`/api/cost-register-items/`).
 */
import type { Job } from '../hooks/useJobPoll'
import { authHeaders, downloadFile, unwrap } from './costing'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''
const ITEMS = `${API_BASE_URL}/api/cost-register-items/`

/** Item money is a decimal string ("12000.00"); rollup money is a JSON number. */
type Money = string | number | null

export type IgceStatus = 'pending' | 'approved' | 'excluded'
export type Verification = 'verified' | 'unverified' | 'not_found'
export type Recurrence = 'one_time' | 'monthly' | 'annual' | 'per_unit' | 'unknown'
export type PriceBasis = 'published' | 'quoted' | 'contract_award' | 'user_entered' | 'estimated'
export type CapturedIn = 'market_research' | 'acquisition_strategy' | 'marketview' | 'manual'

export const RECURRENCES: Recurrence[] = ['one_time', 'monthly', 'annual', 'per_unit', 'unknown']
export const PRICE_BASIS_LABELS: Record<PriceBasis, string> = {
  user_entered: 'User entered',
  quoted: 'Vendor quote',
  published: 'Published price',
  contract_award: 'Prior contract award',
  estimated: 'Government estimate (note required)',
}

export interface RegisterItem {
  id: number
  program: number
  captured_at: string
  captured_in_module: CapturedIn
  source_type: string
  vendor: number | null
  vendor_name: string | null
  product: number | null
  product_name: string | null
  source_citation: string
  description: string
  category: string
  subcategory: string
  amount: Money
  currency: string
  unit_price: Money
  quantity: Money
  unit_of_measure: string
  line_total: Money
  recurrence: Recurrence
  price_basis: PriceBasis
  verification_status: Verification
  confidence: 'high' | 'medium' | 'low'
  as_of_date: string | null
  notes: string
  rationale: string
  igce_status: IgceStatus
  needs_review: boolean
  user_edited: boolean
  is_active: boolean
  duplicate_of: number | null
}

export interface RegisterRollups {
  total: Money
  approved_total: Money
  one_time_subtotal: Money
  recurring_subtotal: Money
  unknown_recurrence_subtotal: Money
  by_category: Record<string, Money>
  completeness: {
    priced_lines: number
    unpriced_lines: number
    needs_review: number
    duplicate_lines: number
    igce: Record<IgceStatus, number>
    statement: string
  }
  label: string
}

export interface IntakeComponent {
  id: string
  label: string
  hint: string
}

export interface IntakeState {
  done: boolean
  skipped: boolean
  notes: string
  /** Components the program expects but gave no planning figure: visible gaps. */
  expected_unpriced: string[]
}

export interface RegisterSummary {
  rollups: RegisterRollups
  categories: Record<string, string>
  intake: IntakeState
  intake_components: IntakeComponent[]
}

export interface IntakeAnswers {
  selections: { id: string; amount: string | null; note: string }[]
  notes: string
  skipped: boolean
}

/** Editable / creatable fields. Money and quantity go as strings or null. */
export interface LineFields {
  description: string
  category: string
  amount: string | null
  unit_price: string | null
  quantity: string | null
  unit_of_measure: string
  recurrence: Recurrence
  price_basis: PriceBasis
  source_citation: string
  notes: string
  rationale: string
}

export interface ParseResult {
  created: number
  /** Figures the register's rules refused (e.g. no price). */
  dropped: number
  /** The model's caveats, and figures it deliberately left out ("Excluded: …"). */
  notes: string[]
}

export interface CaptureCounts {
  captured: number
  unchanged: number
  not_found: number
  products: number
  pm_estimate: number
}

async function post<T>(url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST', headers: authHeaders(), body: body === undefined ? undefined : JSON.stringify(body),
  })
  return unwrap<T>(res)
}

export const costRegisterApi = {
  async list(programId: string | number): Promise<RegisterItem[]> {
    return unwrap(await fetch(`${ITEMS}?program=${programId}`, { headers: authHeaders(false) }))
  },

  async summary(programId: string | number): Promise<RegisterSummary> {
    return unwrap(await fetch(`${ITEMS}summary/?program=${programId}`, { headers: authHeaders(false) }))
  },

  addLine(programId: string | number, fields: Partial<LineFields>): Promise<RegisterItem> {
    return post(ITEMS, { program: programId, ...fields })
  },

  async updateLine(id: number, changes: Partial<LineFields>): Promise<RegisterItem> {
    const res = await fetch(`${ITEMS}${id}/`, {
      method: 'PATCH', headers: authHeaders(), body: JSON.stringify(changes),
    })
    return unwrap(res)
  },

  /** Approve / exclude for the IGCE, or dismiss (soft delete). */
  review(id: number, action: 'approve' | 'exclude' | 'dismiss'): Promise<RegisterItem> {
    return post(`${ITEMS}${id}/${action}/`)
  },

  addCategory(programId: string | number, label: string): Promise<{ key: string; label: string }> {
    return post(`${API_BASE_URL}/api/cost-register-categories/`, { program: programId, label })
  },

  /** Pull costs in from market research (shortlisted products, the PM's estimate). */
  captureFromMarketResearch(programId: string | number): Promise<CaptureCounts> {
    return post(`${ITEMS}capture/?program=${programId}`)
  },

  /** Save the guided intake; planning figures become estimated lines. */
  saveIntake(programId: string | number, answers: IntakeAnswers): Promise<{ created_lines: number; recorded: number }> {
    return post(`${ITEMS}intake/?program=${programId}`, answers)
  },

  /** Upload a cost document (PDF, DOCX, XLSX, CSV) to parse; returns the job to poll. */
  async parseDocument(programId: string | number, file: File): Promise<{ job_id: string }> {
    const body = new FormData()
    body.set('file', file)
    const res = await fetch(`${ITEMS}parse-document/?program=${programId}`, {
      method: 'POST', headers: authHeaders(false), body,
    })
    // A 413 comes from a proxy, before the backend: its body isn't our JSON.
    if (res.status === 413) throw new Error('This file is too large to upload.')
    return unwrap(res)
  },

  /** Poll a parse job until it ends; ``onUpdate`` gets each polled job (progress card). */
  async waitForParse(jobId: string, onUpdate?: (job: Job) => void, intervalMs = 2000): Promise<ParseResult> {
    const deadline = Date.now() + 12 * 60_000
    while (Date.now() < deadline) {
      const job: Job = await unwrap(await fetch(`${API_BASE_URL}/api/ai/jobs/${jobId}/`, { headers: authHeaders(false) }))
      onUpdate?.(job)
      if (job.status === 'completed') return job.result_summary as unknown as ParseResult
      if (job.status === 'failed' || job.status === 'canceled') throw new Error(job.error_message || 'Parsing the cost document failed.')
      await new Promise((r) => setTimeout(r, intervalMs))
    }
    throw new Error('Parsing is taking longer than expected. Refresh the register in a minute.')
  },

  /** Download the register (csv / xlsx) or the IGCE (409 until a line is approved). */
  export(programId: string | number, type: 'csv' | 'xlsx' | 'igce'): Promise<void> {
    const name = type === 'igce' ? 'IGCE.xlsx' : `Cost Register.${type}`
    return downloadFile(`${ITEMS}export/?program=${programId}&type=${type}`, name)
  },
}

export function formatMoney(value: Money): string {
  if (value === null || value === undefined || value === '') return '—'
  return Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}
